import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { type WorkRole, type SystemRole, type UserWorkContext, type EffectiveRole, getEffectiveRole } from '@entities/user';
import { useImpersonationStore } from './impersonationStore';

/**
 * Demo overrides - separate from the authenticated user context
 * These only apply when:
 * 1. Demo mode is enabled
 * 2. User is NOT an admin (ACCOUNT_ADMIN/SYSTEM_ADMIN)
 * 3. Impersonation is NOT active
 */
interface DemoOverrides {
  work_role?: WorkRole;
  system_role?: SystemRole;
  branch_id?: string;
  enabled: boolean;
}

interface WorkRoleState {
  // Current user's work context (the authenticated user or default demo user)
  currentContext: UserWorkContext | null;
  
  // Demo toolbar overrides (separate from auth context)
  demoOverrides: DemoOverrides;
  
  // Set the full context (typically on login)
  setCurrentContext: (context: UserWorkContext | null) => void;
  
  // Demo override setters (only for toolbar use)
  setDemoWorkRole: (role: WorkRole) => void;
  setDemoSystemRole: (role: SystemRole) => void;
  setDemoBranchId: (branchId: string | undefined) => void;
  enableDemoOverrides: (enabled: boolean) => void;
  clearDemoOverrides: () => void;
  
  // Legacy setters that modify currentContext (for real auth updates)
  setWorkRole: (role: WorkRole) => void;
  setSystemRole: (role: SystemRole) => void;
  setBranchId: (branchId: string | undefined) => void;
  setAccountId: (accountId: string) => void;
  setAssignedQueueIds: (queueIds: string[]) => void;
  
  // Getters for common checks - these use the EFFECTIVE context
  getWorkRole: () => WorkRole;
  getSystemRole: () => SystemRole;
  getEffectiveRole: () => EffectiveRole;
  getBranchId: () => string | undefined;
  getAccountId: () => string;
  getUserId: () => string;
  getAssignedQueueIds: () => string[];
  
  // Get the real (non-impersonated, non-demo-overridden) context for security checks
  getRealContext: () => UserWorkContext | null;
  getRealSystemRole: () => SystemRole;
  
  // Check if demo overrides are active and applicable
  areDemoOverridesActive: () => boolean;
  
  // Check if the real user is an admin (ignoring overrides)
  isRealUserAdmin: () => boolean;
  
  // Role check helpers
  isBranchUser: () => boolean;
  isTechnician: () => boolean;
  isTechManager: () => boolean;
  isAccountAdmin: () => boolean;
  isSystemAdmin: () => boolean;
  hasElevatedAccess: () => boolean;
  isQueueUser: (queueId: string) => boolean;
  
  // Visibility check helpers
  canViewAllBranches: () => boolean;
  canViewInternalNotes: () => boolean;
}

// Default demo context - simulates an account admin for full access during development
// Using 'per-7' as demo user ID to match mock ticket assignments for "My Tickets" demo
const DEFAULT_CONTEXT: UserWorkContext = {
  id: 'per-7', // Matches mock technician with assigned tickets
  account_id: '1',
  branch_id: undefined, // Admins can see all branches
  work_role: 'TECH_MANAGER',
  system_role: 'ACCOUNT_ADMIN',
  assigned_queue_ids: ['queue-1', 'queue-2', 'queue-3', 'queue-4', 'queue-5'],
};

const DEFAULT_DEMO_OVERRIDES: DemoOverrides = {
  enabled: false,
  work_role: undefined,
  system_role: undefined,
  branch_id: undefined,
};

/**
 * Get the effective context following the priority chain:
 * 1. Impersonation (highest priority) - admin acting as another user
 * 2. Demo Overrides (only if not admin and not impersonating)
 * 3. Base Context (authenticated user or default)
 */
function getEffectiveContext(
  baseContext: UserWorkContext | null, 
  demoOverrides: DemoOverrides
): UserWorkContext | null {
  // Use default context when base is null (e.g. post-logout persist, before auth loads)
  const ctx = baseContext ?? DEFAULT_CONTEXT;

  // Priority 1: Check impersonation first
  const impersonationState = useImpersonationStore.getState();
  if (impersonationState.isImpersonating()) {
    // Return impersonated context - ignores demo overrides entirely
    return impersonationState.getEffectiveContext(ctx);
  }
  
  // Priority 2: Check if demo overrides are applicable
  // Demo overrides are BLOCKED if the real user is an admin
  if (demoOverrides.enabled) {
    const isRealAdmin = 
      ctx.system_role === 'ACCOUNT_ADMIN' || 
      ctx.system_role === 'SYSTEM_ADMIN';
    
    if (!isRealAdmin) {
      // Apply demo overrides to base context
      return {
        ...ctx,
        work_role: demoOverrides.work_role ?? ctx.work_role,
        system_role: demoOverrides.system_role ?? ctx.system_role,
        branch_id: demoOverrides.branch_id ?? ctx.branch_id,
      };
    }
    // If admin, demo overrides are ignored - fall through to base context
  }
  
  // Priority 3: Return base context
  return ctx;
}

export const useWorkRoleStore = create<WorkRoleState>()(
  persist(
    (set, get) => ({
      currentContext: DEFAULT_CONTEXT,
      demoOverrides: DEFAULT_DEMO_OVERRIDES,
      
      setCurrentContext: (context) => set({ currentContext: context }),
      
      // Demo override setters
      setDemoWorkRole: (role) => set((state) => ({
        demoOverrides: { ...state.demoOverrides, work_role: role, enabled: true }
      })),
      
      setDemoSystemRole: (role) => set((state) => ({
        demoOverrides: { ...state.demoOverrides, system_role: role, enabled: true }
      })),
      
      setDemoBranchId: (branchId) => set((state) => ({
        demoOverrides: { ...state.demoOverrides, branch_id: branchId, enabled: true }
      })),
      
      enableDemoOverrides: (enabled) => set((state) => ({
        demoOverrides: { ...state.demoOverrides, enabled }
      })),
      
      clearDemoOverrides: () => set({ demoOverrides: DEFAULT_DEMO_OVERRIDES }),
      
      // Legacy setters - modify currentContext directly (for real auth updates)
      setWorkRole: (role) => set((state) => ({
        currentContext: state.currentContext 
          ? { ...state.currentContext, work_role: role }
          : { ...DEFAULT_CONTEXT, work_role: role }
      })),
      
      setSystemRole: (role) => set((state) => ({
        currentContext: state.currentContext
          ? { ...state.currentContext, system_role: role }
          : { ...DEFAULT_CONTEXT, system_role: role }
      })),
      
      setBranchId: (branchId) => set((state) => ({
        currentContext: state.currentContext
          ? { ...state.currentContext, branch_id: branchId }
          : { ...DEFAULT_CONTEXT, branch_id: branchId }
      })),
      
      setAccountId: (accountId) => set((state) => ({
        currentContext: state.currentContext
          ? { ...state.currentContext, account_id: accountId }
          : { ...DEFAULT_CONTEXT, account_id: accountId }
      })),
      
      setAssignedQueueIds: (queueIds) => set((state) => ({
        currentContext: state.currentContext
          ? { ...state.currentContext, assigned_queue_ids: queueIds }
          : { ...DEFAULT_CONTEXT, assigned_queue_ids: queueIds }
      })),
      
      // Getters use effective context
      getWorkRole: () => {
        const { currentContext, demoOverrides } = get();
        const effectiveCtx = getEffectiveContext(currentContext, demoOverrides);
        return effectiveCtx?.work_role ?? 'BRANCH_USER';
      },
      
      getSystemRole: () => {
        const { currentContext, demoOverrides } = get();
        const effectiveCtx = getEffectiveContext(currentContext, demoOverrides);
        return effectiveCtx?.system_role ?? 'NORMAL_USER';
      },
      
      getEffectiveRole: () => {
        const { currentContext, demoOverrides } = get();
        const effectiveCtx = getEffectiveContext(currentContext, demoOverrides);
        if (!effectiveCtx) return 'BRANCH_USER';
        return getEffectiveRole(effectiveCtx.work_role, effectiveCtx.system_role);
      },
      
      getBranchId: () => {
        const { currentContext, demoOverrides } = get();
        const effectiveCtx = getEffectiveContext(currentContext, demoOverrides);
        return effectiveCtx?.branch_id;
      },
      
      getAccountId: () => {
        const { currentContext, demoOverrides } = get();
        const effectiveCtx = getEffectiveContext(currentContext, demoOverrides);
        return effectiveCtx?.account_id ?? '1';
      },
      
      getUserId: () => {
        const { currentContext, demoOverrides } = get();
        const effectiveCtx = getEffectiveContext(currentContext, demoOverrides);
        return effectiveCtx?.id ?? '';
      },
      
      getAssignedQueueIds: () => {
        const { currentContext, demoOverrides } = get();
        const effectiveCtx = getEffectiveContext(currentContext, demoOverrides);
        return effectiveCtx?.assigned_queue_ids ?? [];
      },
      
      // Get real (non-overridden) context for security gates
      getRealContext: () => get().currentContext,
      
      getRealSystemRole: () => get().currentContext?.system_role ?? 'NORMAL_USER',
      
      // Check if demo overrides are currently active
      areDemoOverridesActive: () => {
        const { currentContext, demoOverrides } = get();
        if (!demoOverrides.enabled) return false;
        
        // Check if impersonation is active (blocks demo overrides)
        const impersonationState = useImpersonationStore.getState();
        if (impersonationState.isImpersonating()) return false;
        
        // Check if real user is admin (blocks demo overrides)
        if (currentContext) {
          const isRealAdmin = 
            currentContext.system_role === 'ACCOUNT_ADMIN' || 
            currentContext.system_role === 'SYSTEM_ADMIN';
          if (isRealAdmin) return false;
        }
        
        return true;
      },
      
      // Check if the real (base) user is an admin
      isRealUserAdmin: () => {
        const ctx = get().currentContext;
        return ctx?.system_role === 'ACCOUNT_ADMIN' || ctx?.system_role === 'SYSTEM_ADMIN';
      },
      
      // Role checks based on effective work_role
      isBranchUser: () => {
        const { currentContext, demoOverrides } = get();
        const effectiveCtx = getEffectiveContext(currentContext, demoOverrides);
        return effectiveCtx?.work_role === 'BRANCH_USER';
      },
      
      isTechnician: () => {
        const { currentContext, demoOverrides } = get();
        const effectiveCtx = getEffectiveContext(currentContext, demoOverrides);
        return effectiveCtx?.work_role === 'TECHNICIAN';
      },
      
      isTechManager: () => {
        const { currentContext, demoOverrides } = get();
        const effectiveCtx = getEffectiveContext(currentContext, demoOverrides);
        return effectiveCtx?.work_role === 'TECH_MANAGER';
      },
      
      // Role checks based on effective system_role
      isAccountAdmin: () => {
        const { currentContext, demoOverrides } = get();
        const effectiveCtx = getEffectiveContext(currentContext, demoOverrides);
        return effectiveCtx?.system_role === 'ACCOUNT_ADMIN';
      },
      
      isSystemAdmin: () => {
        const { currentContext, demoOverrides } = get();
        const effectiveCtx = getEffectiveContext(currentContext, demoOverrides);
        return effectiveCtx?.system_role === 'SYSTEM_ADMIN';
      },
      
      // Check for elevated access (tech manager, account admin, or system admin)
      hasElevatedAccess: () => {
        const { currentContext, demoOverrides } = get();
        const effectiveCtx = getEffectiveContext(currentContext, demoOverrides);
        if (!effectiveCtx) return false;
        return (
          effectiveCtx.work_role === 'TECH_MANAGER' ||
          effectiveCtx.system_role === 'ACCOUNT_ADMIN' ||
          effectiveCtx.system_role === 'SYSTEM_ADMIN'
        );
      },
      
      isQueueUser: (queueId: string) => {
        const { currentContext, demoOverrides } = get();
        const effectiveCtx = getEffectiveContext(currentContext, demoOverrides);
        const queueIds = effectiveCtx?.assigned_queue_ids ?? [];
        return queueIds.includes(queueId);
      },
      
      // Visibility helpers
      canViewAllBranches: () => {
        const effectiveRole = get().getEffectiveRole();
        // Only BRANCH_USER is restricted to their branch
        return effectiveRole !== 'BRANCH_USER';
      },
      
      canViewInternalNotes: () => {
        const effectiveRole = get().getEffectiveRole();
        // Everyone except BRANCH_USER can see internal notes
        return effectiveRole !== 'BRANCH_USER';
      },
    }),
    {
      name: 'work-role-storage',
      partialize: (state) => ({
        currentContext: state.currentContext,
        demoOverrides: state.demoOverrides,
      }),
    }
  )
);
